import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ChevronDown, User, Search, Filter, X, Download, Loader2,
  Clock, Coffee, Mic2, Users, Sparkles, Building2, MessageSquare, Lightbulb,
  CalendarPlus, CheckCircle, Mail, MapPin, ExternalLink, Package
} from 'lucide-react';
import { useSiteConfig } from '../../context/SiteConfigContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============ HERO BLOCK ============
export const HeroBlock = ({ content, settings }) => {
  const { getEventDateTime, event, isPageActive } = useSiteConfig();
  const eventDate = getEventDateTime();
  
  // Countdown logic
  const [countdown, setCountdown] = React.useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  React.useEffect(() => {
    if (!content?.show_countdown) return;
    
    const calculateCountdown = () => {
      const now = new Date();
      const diff = eventDate - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    };
    
    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, [eventDate, content?.show_countdown]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <section 
      className={`relative flex flex-col items-center justify-center px-6 ${settings?.full_height ? 'min-h-screen' : 'py-24'}`}
      data-testid="hero-block"
    >
      <div className="text-center max-w-5xl mx-auto z-10">
        {content?.date_text && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary mb-6">
            <span className="text-sm font-medium tracking-wider">{content.date_text}</span>
          </div>
        )}
        
        <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 leading-tight">
          {content?.title || 'Conference Title'}
        </h1>
        
        <p className="font-outfit text-lg sm:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto mb-6">
          {content?.subtitle || 'Conference subtitle goes here'}
        </p>
        
        <p className="text-xl sm:text-2xl font-semibold text-primary mb-8">
          {formatDate(eventDate)}
          {event?.venue?.name && ` • ${event.venue.name}`}
        </p>

        {content?.show_countdown && (
          <div className="flex justify-center gap-4 md:gap-8 mb-10">
            {[
              { value: countdown.days, label: 'Days' },
              { value: countdown.hours, label: 'Hours' },
              { value: countdown.minutes, label: 'Minutes' },
              { value: countdown.seconds, label: 'Seconds' }
            ].map((item) => (
              <div key={item.label} className="glass-card rounded-xl p-4 min-w-[80px] md:min-w-[100px]">
                <div className="text-3xl md:text-4xl font-bold text-primary">{String(item.value).padStart(2, '0')}</div>
                <div className="text-xs md:text-sm text-muted-foreground">{item.label}</div>
              </div>
            ))}
          </div>
        )}

        {content?.cta_buttons?.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {content.cta_buttons.filter(btn => isPageActive(btn.path)).map((btn, idx) => (
              <Link
                key={idx}
                to={btn.path || '/'}
                className={`group flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300 ${
                  btn.style === 'primary' 
                    ? 'bg-primary text-primary-foreground hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/25'
                    : 'glass-card hover:bg-white/10'
                }`}
              >
                {btn.text}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        )}
      </div>

      {settings?.show_scroll_indicator && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-muted-foreground" />
        </div>
      )}
    </section>
  );
};

// ============ TEXT BLOCK ============
export const TextBlock = ({ content, settings }) => {
  const alignment = content?.alignment || 'left';
  const alignClass = alignment === 'center' ? 'text-center mx-auto' : alignment === 'right' ? 'text-right ml-auto' : '';
  
  return (
    <section 
      className={`py-16 px-6 md:px-12 lg:px-24 ${settings?.background === 'muted' ? 'bg-muted/30' : ''}`}
      data-testid="text-block"
    >
      <div className={`max-w-4xl ${alignClass}`}>
        {content?.heading && (
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6">
            {content.heading}
          </h2>
        )}
        {content?.body && (
          <div 
            className="prose prose-invert max-w-none text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: content.body }}
          />
        )}
      </div>
    </section>
  );
};

// ============ CTA BLOCK ============
export const CTABlock = ({ content, settings }) => {
  const { isPageActive } = useSiteConfig();
  const visibleButtons = (content?.buttons || []).filter(btn => isPageActive(btn.path));
  return (
    <section 
      className={`py-20 px-6 md:px-12 lg:px-24 ${
        settings?.background === 'primary' ? 'bg-primary/10' : 'bg-muted/30'
      }`}
      data-testid="cta-block"
    >
      <div className="max-w-4xl mx-auto text-center">
        {content?.title && (
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4">
            {content.title}
          </h2>
        )}
        {content?.subtitle && (
          <p className="text-lg text-muted-foreground mb-8">
            {content.subtitle}
          </p>
        )}
        {visibleButtons.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {visibleButtons.map((btn, idx) => (
              <Link
                key={idx}
                to={btn.path || '/'}
                className={`group flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300 ${
                  btn.style === 'primary' 
                    ? 'bg-primary text-primary-foreground hover:-translate-y-1'
                    : 'glass-card hover:bg-white/10'
                }`}
              >
                {btn.text}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ============ IMAGE BLOCK ============
export const ImageBlock = ({ content, settings }) => {
  return (
    <section 
      className={`py-12 px-6 md:px-12 lg:px-24 ${settings?.full_width ? '' : 'max-w-6xl mx-auto'}`}
      data-testid="image-block"
    >
      {content?.url && (
        <div className={`relative ${settings?.rounded ? 'rounded-2xl overflow-hidden' : ''}`}>
          <img 
            src={content.url} 
            alt={content.alt || 'Image'} 
            className="w-full h-auto object-cover"
          />
          {content?.caption && (
            <p className="mt-4 text-sm text-muted-foreground text-center">
              {content.caption}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

// ============ CARDS BLOCK ============
export const CardsBlock = ({ content, settings }) => {
  const cards = content?.cards || [];
  const columns = settings?.columns || 3;
  
  return (
    <section 
      className={`py-16 px-6 md:px-12 lg:px-24 ${settings?.background === 'muted' ? 'bg-muted/30' : ''}`}
      data-testid="cards-block"
    >
      <div className="max-w-6xl mx-auto">
        {content?.title && (
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-center">
            {content.title}
          </h2>
        )}
        {content?.subtitle && (
          <p className="text-lg text-muted-foreground mb-12 text-center max-w-2xl mx-auto">
            {content.subtitle}
          </p>
        )}
        <div className={`grid gap-6 ${
          columns === 2 ? 'md:grid-cols-2' : 
          columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 
          'md:grid-cols-2 lg:grid-cols-3'
        }`}>
          {cards.map((card, idx) => (
            <div key={idx} className="glass-card rounded-xl p-6 hover:bg-white/10 transition-colors">
              {card.icon && (
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center mb-4">
                  <span className="text-2xl">{card.icon}</span>
                </div>
              )}
              {card.title && (
                <h3 className="font-semibold text-lg mb-2">{card.title}</h3>
              )}
              {card.description && (
                <p className="text-muted-foreground text-sm">{card.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ SPACER BLOCK ============
export const SpacerBlock = ({ settings }) => {
  const height = settings?.height || 'medium';
  const heightClass = height === 'small' ? 'h-8' : height === 'large' ? 'h-32' : 'h-16';
  
  return <div className={heightClass} data-testid="spacer-block" />;
};

// ============ DIVIDER BLOCK ============
export const DividerBlock = ({ settings }) => {
  return (
    <div className="py-8 px-6 md:px-12 lg:px-24" data-testid="divider-block">
      <div className={`max-w-6xl mx-auto border-t ${settings?.style === 'dashed' ? 'border-dashed' : ''} border-border`} />
    </div>
  );
};

// ============ STATS BLOCK ============
export const StatsBlock = ({ content, settings }) => {
  const stats = content?.stats || [];
  const columns = settings?.columns || 4;

  return (
    <section
      className={`py-20 px-6 md:px-12 lg:px-24 ${settings?.background === 'muted' ? 'bg-muted/30' : ''}`}
      data-testid="stats-block"
    >
      <div className="max-w-5xl mx-auto">
        {content?.title && (
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-center">{content.title}</h2>
        )}
        {content?.subtitle && (
          <p className="text-lg text-muted-foreground mb-12 text-center max-w-3xl mx-auto">{content.subtitle}</p>
        )}
        <div className="glass-card rounded-3xl p-8 md:p-12">
          <div className={`grid grid-cols-2 ${columns >= 4 ? 'md:grid-cols-4' : columns === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-8 text-center`}>
            {stats.map((stat, index) => (
              <div key={index}>
                <div className="font-outfit text-3xl md:text-4xl font-bold text-gradient-quantum mb-2">{stat.value}</div>
                <div className="font-medium text-sm mb-1">{stat.label}</div>
                {stat.source && <div className="text-xs text-muted-foreground">{stat.source}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ SPEAKER GRID BLOCK ============
export const SpeakerGridBlock = ({ content, settings }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [showFilters, setShowFilters] = React.useState(false);
  const [cmsSpeakers, setCmsSpeakers] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchSpeakers = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/speakers`);
        if (response.ok) setCmsSpeakers(await response.json());
      } catch (err) { console.error('Failed to fetch speakers:', err); }
      finally { setIsLoading(false); }
    };
    fetchSpeakers();
  }, []);

  const allSpeakers = React.useMemo(() => {
    return cmsSpeakers.map(s => ({
      name: s.name,
      role: s.title || '',
      affiliation: s.organization || '',
      image: s.photo_url,
      imagePosition: s.image_position || 'center top',
      bio: s.bio || '',
      tags: s.is_featured ? ['Keynote Speaker'] : ['Panelist'],
      isKeynote: s.is_featured || false,
      order: s.order || 0
    }));
  }, [cmsSpeakers]);

  const allTags = React.useMemo(() => {
    const tags = new Set();
    allSpeakers.forEach(s => s.tags.forEach(t => tags.add(t)));
    return Array.from(tags).sort();
  }, [allSpeakers]);

  const filteredSpeakers = React.useMemo(() => {
    return allSpeakers
      .filter(s => {
        const matchesSearch = !searchQuery || 
          s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.affiliation.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.role.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTags = selectedTags.length === 0 || selectedTags.some(t => s.tags.includes(t));
        return matchesSearch && matchesTags;
      })
      .sort((a, b) => a.order - b.order);
  }, [allSpeakers, searchQuery, selectedTags]);

  const keynoteSpeakers = filteredSpeakers.filter(s => s.isKeynote).slice(0, 3);
  const otherSpeakers = filteredSpeakers.filter(s => !s.isKeynote || !keynoteSpeakers.find(k => k.name === s.name));

  const toggleTag = (tag) => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  const getTagColor = (tag) => {
    if (tag.includes('Keynote')) return 'bg-primary/20 text-primary';
    if (tag.includes('Moderator')) return 'bg-secondary/20 text-secondary';
    return 'bg-muted text-muted-foreground';
  };

  const SpeakerCard = ({ speaker }) => (
    <div className="glass-card rounded-xl overflow-hidden hover:border-primary/50 transition-all duration-300 group" data-testid={`speaker-card-${speaker.name.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="aspect-square overflow-hidden bg-muted relative">
        {speaker.image ? (
          <img src={speaker.image} alt={speaker.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" style={{ objectPosition: speaker.imagePosition }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
            <User className="w-20 h-20 text-muted-foreground/30" />
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex flex-wrap gap-2 mb-3">
          {speaker.tags.map((tag, i) => (
            <span key={i} className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTagColor(tag)}`}>{tag}</span>
          ))}
        </div>
        <h3 className="font-outfit text-xl font-bold mb-1">{speaker.name}</h3>
        <p className="text-sm text-primary font-medium mb-1 uppercase tracking-wide">{speaker.role}</p>
        <p className="text-sm text-muted-foreground mb-4">{speaker.affiliation}</p>
        <p className="text-muted-foreground text-sm line-clamp-4">{speaker.bio}</p>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <section className="py-16 px-6 md:px-12 lg:px-24" data-testid="speaker-grid-block">
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      </section>
    );
  }

  if (allSpeakers.length === 0) return null;

  return (
    <section className={`py-8 px-6 md:px-12 lg:px-24 ${settings?.background === 'muted' ? 'bg-muted/30' : ''}`} data-testid="speaker-grid-block">
      {content?.show_search && (
        <div className="max-w-6xl mx-auto mb-12">
          <div className="glass-card rounded-xl p-4 md:p-6">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="relative flex-1 w-full md:max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input type="text" placeholder="Search speakers..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors" data-testid="speaker-search-input" />
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${showFilters || selectedTags.length > 0 ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`} data-testid="toggle-filters-btn">
                <Filter className="w-4 h-4" /> Filters {selectedTags.length > 0 && <span className="ml-1 px-2 py-0.5 text-xs bg-white/20 rounded-full">{selectedTags.length}</span>}
              </button>
            </div>
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex flex-wrap gap-2">
                  {allTags.map(tag => (
                    <button key={tag} onClick={() => toggleTag(tag)} className={`px-3 py-1.5 text-sm rounded-full transition-all ${selectedTags.includes(tag) ? 'bg-primary text-primary-foreground' : `${getTagColor(tag)} hover:opacity-80`}`}>{tag}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {content?.show_keynote_section && keynoteSpeakers.length > 0 && (
        <div className="max-w-7xl mx-auto mb-20" data-testid="keynote-speakers">
          <h2 className="font-outfit text-2xl font-semibold text-center mb-12">
            <span className="text-gradient-housing">{content?.keynote_title || 'Hosts'}</span> and Moderators
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {keynoteSpeakers.map((speaker, i) => <SpeakerCard key={i} speaker={speaker} />)}
          </div>
        </div>
      )}

      {otherSpeakers.length > 0 && (
        <div className="max-w-6xl mx-auto" data-testid="featured-speakers">
          <h2 className="font-outfit text-2xl font-semibold text-center mb-12">
            {content?.grid_title || 'Featured'} <span className="text-gradient-quantum">Speakers</span>
          </h2>
          <div className={`grid md:grid-cols-2 lg:grid-cols-${settings?.columns || 3} gap-6`}>
            {otherSpeakers.map((speaker, i) => <SpeakerCard key={i} speaker={speaker} />)}
          </div>
        </div>
      )}

      {filteredSpeakers.length === 0 && (
        <div className="max-w-2xl mx-auto text-center py-12">
          <User className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-outfit text-xl font-semibold mb-2">No speakers found</h3>
          <button onClick={() => { setSearchQuery(''); setSelectedTags([]); }} className="text-primary hover:underline font-medium">Clear all filters</button>
        </div>
      )}
    </section>
  );
};

// ============ AGENDA SCHEDULE BLOCK ============
export const AgendaScheduleBlock = ({ content, settings }) => {
  const [cmsAgenda, setCmsAgenda] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const conferenceDate = new Date('2026-04-17');

  React.useEffect(() => {
    const fetchAgenda = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/agenda`);
        if (response.ok) setCmsAgenda(await response.json());
      } catch (err) { console.error('Failed to fetch agenda:', err); }
      finally { setIsLoading(false); }
    };
    fetchAgenda();
  }, []);

  const defaultSchedule = [
    { time: '08:00', endTime: '09:00', title: 'Registration & Welcome Coffee', type: 'networking', description: 'Check in, grab your badge, and connect with fellow attendees over breakfast.' },
    { time: '09:00', endTime: '09:30', title: 'Opening Keynote', type: 'keynote', speaker: 'Vernand Morency', description: 'Setting the vision for global affordable housing transformation.' },
    { time: '09:30', endTime: '10:30', title: 'The Future of Quantum AI in Real Estate', type: 'session', description: 'Exploring how quantum computing and AI are revolutionizing property analysis.' },
    { time: '10:30', endTime: '11:00', title: 'Networking Break', type: 'networking', description: 'Connect with speakers and attendees.' },
    { time: '11:00', endTime: '12:00', title: 'Panel: Athletes as Impact Investors', type: 'panel', description: 'How professional athletes are leveraging their platforms to address affordable housing.' },
    { time: '12:00', endTime: '13:30', title: 'Lunch & Networking', type: 'networking', description: 'Gourmet lunch with themed discussion tables.' },
    { time: '13:30', endTime: '14:30', title: 'NexusImpact.AI Platform Launch', type: 'keynote', description: 'Official unveiling of the quantum enabled social impact investment platform.' },
    { time: '14:30', endTime: '15:30', title: 'Workshop: Building Sustainable Communities', type: 'workshop', description: 'Interactive session on designing carbon neutral affordable housing.' },
    { time: '15:30', endTime: '16:00', title: 'Afternoon Break', type: 'networking', description: 'Refreshments and networking.' },
    { time: '16:00', endTime: '17:00', title: 'Fireside Chat: Policy & Innovation', type: 'session', description: 'Discussion on regulatory frameworks supporting affordable housing innovation.' },
    { time: '17:00', endTime: '17:30', title: 'Closing Remarks & Call to Action', type: 'keynote', description: 'Summarizing key takeaways and the path forward.' },
    { time: '17:30', endTime: '19:30', title: 'Evening Reception', type: 'networking', description: 'Cocktails, live music, and continued networking.' }
  ];

  const schedule = React.useMemo(() => {
    if (cmsAgenda.length > 0) {
      return cmsAgenda.map(item => {
        const timeParts = item.time.split(/\s*[-–]\s*/);
        return { time: timeParts[0] || item.time, endTime: timeParts[1] || timeParts[0], title: item.title, type: item.session_type, speaker: item.speakers?.join(', '), description: item.description || '' };
      });
    }
    return defaultSchedule;
  }, [cmsAgenda]);

  const generateICS = () => {
    const formatDateICS = (date, time) => {
      const [hours, minutes] = time.split(':');
      const d = new Date(date);
      d.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    const events = schedule.filter(i => i.type !== 'networking' || i.title.includes('Reception')).map(item => {
      const start = formatDateICS(conferenceDate, item.time);
      const end = formatDateICS(conferenceDate, item.endTime);
      return `BEGIN:VEVENT\nDTSTART:${start}\nDTEND:${end}\nSUMMARY:${item.title}\nDESCRIPTION:${item.description.replace(/,/g, '\\,')}\nLOCATION:Tsai Auditorium\\, Harvard University\nEND:VEVENT`;
    });
    const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//The Jock Tax Summit//EN\n${events.join('\n')}\nEND:VCALENDAR`;
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'jock-tax-summit-2026.ics';
    link.click();
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'keynote': return 'border-l-primary bg-primary/5';
      case 'session': return 'border-l-secondary bg-secondary/5';
      case 'panel': return 'border-l-accent bg-accent/5';
      case 'workshop': return 'border-l-green-500 bg-green-500/5';
      case 'networking': return 'border-l-muted-foreground bg-muted/30';
      default: return 'border-l-border';
    }
  };

  const iconMap = { keynote: Mic2, session: Sparkles, panel: Users, networking: Coffee, workshop: Building2 };

  if (isLoading) {
    return <section className="py-16 px-6 md:px-12 lg:px-24" data-testid="agenda-schedule-block"><div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></section>;
  }

  return (
    <section className={`py-8 px-6 md:px-12 lg:px-24 ${settings?.background === 'muted' ? 'bg-muted/30' : ''}`} data-testid="agenda-schedule-block">
      {content?.show_legend && (
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-wrap justify-center gap-4">
            {[['Keynote', 'bg-primary'], ['Session', 'bg-secondary'], ['Panel', 'bg-accent'], ['Workshop', 'bg-green-500'], ['Networking', 'bg-muted-foreground']].map(([label, color]) => (
              <div key={label} className="flex items-center gap-2 text-sm"><div className={`w-3 h-3 rounded-full ${color}`} /><span>{label}</span></div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto mb-8" data-testid="schedule-section">
        <div className="space-y-4">
          {schedule.map((item, index) => {
            const IconComp = iconMap[item.type] || Sparkles;
            return (
              <div key={index} className={`glass-card rounded-xl p-6 border-l-4 ${getTypeColor(item.type)} transition-all hover:-translate-y-1`} data-testid={`agenda-item-${index}`}>
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex items-center gap-3 md:w-40 flex-shrink-0">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <span className="font-mono text-lg font-semibold">{item.time} – {item.endTime}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-background/50 flex items-center justify-center flex-shrink-0">
                        <IconComp className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-outfit text-xl font-semibold mb-1">{item.title}</h3>
                        {item.speaker && <p className="text-primary font-medium text-sm mb-2">{item.speaker}</p>}
                        <p className="text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {content?.show_calendar_download && (
        <div className="max-w-4xl mx-auto flex justify-center gap-4">
          <button onClick={generateICS} className="inline-flex items-center gap-2 px-6 py-3 glass-card rounded-full font-semibold transition-all hover:bg-white/10" data-testid="add-to-calendar-btn">
            <CalendarPlus className="w-5 h-5" /> Add to Calendar
          </button>
        </div>
      )}
    </section>
  );
};

// ============ PACKAGES GRID BLOCK ============
export const PackagesGridBlock = ({ content, settings }) => {
  const [packages, setPackages] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/packages`);
        if (response.ok) setPackages(await response.json());
      } catch (err) { console.error('Failed to fetch packages:', err); }
      finally { setIsLoading(false); }
    };
    fetchPackages();
  }, []);

  if (isLoading || packages.length === 0) return null;

  const tierStyles = [
    { gradient: 'from-amber-500/20 via-amber-600/10 to-transparent', border: 'border-amber-500/50' },
    { gradient: 'from-slate-300/20 via-slate-400/10 to-transparent', border: 'border-slate-400/50' },
    { gradient: 'from-yellow-500/20 via-yellow-600/10 to-transparent', border: 'border-yellow-500/50' },
    { gradient: 'from-gray-400/20 via-gray-500/10 to-transparent', border: 'border-gray-400/50' }
  ];

  return (
    <section className={`py-24 px-6 md:px-12 lg:px-24 ${settings?.background === 'muted' ? 'bg-muted/30' : ''}`} data-testid="packages-grid-block">
      <div className="max-w-7xl mx-auto">
        {content?.section_label && (
          <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block text-center">{content.section_label}</span>
        )}
        {content?.title && (
          <h2 className="font-playfair text-3xl md:text-5xl font-bold tracking-tight text-center mb-4">
            {content.title.split(' ').map((w, i, a) => i === a.length - 1 ? <span key={i} className="text-gradient-quantum">{w}</span> : w + ' ')}
          </h2>
        )}
        {content?.subtitle && <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-center mb-12">{content.subtitle}</p>}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg, index) => {
            const style = tierStyles[index] || tierStyles[3];
            return (
              <div key={pkg.id} className={`relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 ${pkg.is_featured ? 'ring-2 ring-primary lg:scale-105 z-10' : ''}`}>
                <div className={`absolute inset-0 bg-gradient-to-b ${style.gradient}`} />
                <div className={`relative glass-card rounded-2xl p-6 h-full border ${style.border} flex flex-col`}>
                  <div className="text-center pt-4 mb-4">
                    <h3 className="font-outfit text-xl font-bold">{pkg.name}</h3>
                    {pkg.tagline && <p className="text-sm text-muted-foreground mt-1">{pkg.tagline}</p>}
                  </div>
                  <div className="text-center mb-4">
                    <div className="font-outfit text-3xl font-bold text-gradient-quantum">{pkg.price_display || `$${pkg.price?.toLocaleString()}`}</div>
                  </div>
                  {pkg.benefits?.length > 0 && (
                    <ul className="space-y-2.5 mb-6 flex-1">
                      {pkg.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm">
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" /><span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <a href="#contact-form" className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold transition-all duration-300 hover:-translate-y-0.5 mt-auto ${pkg.is_featured ? 'bg-primary text-primary-foreground' : 'glass-card border border-border hover:border-primary/50'}`}>
                    Request Info <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ CONTACT FORM BLOCK ============
export const ContactFormBlock = ({ content, settings }) => {
  const [formData, setFormData] = React.useState({ name: '', organization: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const subject = encodeURIComponent(content?.email_subject || 'Inquiry - The Jock Tax Summit');
    const body = encodeURIComponent(`Name: ${formData.name}\nOrganization: ${formData.organization}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`);
    window.location.href = `mailto:${content?.email_to || 'contact@globalintech.ai'}?subject=${subject}&body=${body}`;
    setTimeout(() => { setIsSubmitting(false); setSubmitted(true); }, 1000);
  };

  return (
    <section id="contact-form" className={`py-24 px-6 md:px-12 lg:px-24 ${settings?.background === 'muted' ? 'bg-muted/30' : ''}`} data-testid="contact-form-block">
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            {content?.section_label && <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">{content.section_label}</span>}
            {content?.title && <h2 className="font-playfair text-3xl md:text-4xl font-bold tracking-tight mb-6">{content.title}</h2>}
            {content?.subtitle && <p className="text-lg text-muted-foreground leading-relaxed mb-8">{content.subtitle}</p>}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Mail className="w-5 h-5 text-primary" /></div>
                <a href={`mailto:${content?.email_to || 'contact@globalintech.ai'}`} className="font-medium hover:text-primary transition-colors">{content?.email_to || 'contact@globalintech.ai'}</a>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><MapPin className="w-5 h-5 text-primary" /></div>
                <p className="font-medium">Tsai Auditorium, Harvard University</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-green-400" /></div>
                <h3 className="font-outfit text-xl font-bold mb-2">Request Received</h3>
                <p className="text-muted-foreground">Your email client should have opened.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {['name', 'organization', 'email', 'phone'].map(field => (
                  <div key={field}>
                    <label className="block text-sm font-medium mb-2 capitalize">{field === 'phone' ? 'Phone (optional)' : `${field} *`}</label>
                    <input type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'} required={field !== 'phone'} value={formData[field]}
                      onChange={(e) => setFormData(p => ({ ...p, [field]: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                      data-testid={`contact-${field}-input`} />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <textarea rows={4} value={formData.message} onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors resize-none"
                    data-testid="contact-message-input" />
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-primary-foreground rounded-xl font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50" data-testid="contact-submit-btn">
                  {isSubmitting ? 'Preparing...' : (content?.button_text || 'Send Inquiry')} <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ BLOCK RENDERER ============
const blockComponents = {
  hero: HeroBlock,
  text: TextBlock,
  cta: CTABlock,
  image: ImageBlock,
  cards: CardsBlock,
  spacer: SpacerBlock,
  divider: DividerBlock,
  stats: StatsBlock,
  speaker_grid: SpeakerGridBlock,
  agenda_schedule: AgendaScheduleBlock,
  packages_grid: PackagesGridBlock,
  contact_form: ContactFormBlock,
};

export const BlockRenderer = ({ block }) => {
  if (!block?.visible) return null;
  
  const Component = blockComponents[block.type];
  if (!Component) {
    console.warn(`Unknown block type: ${block.type}`);
    return null;
  }
  
  return <Component content={block.content} settings={block.settings} />;
};

export default BlockRenderer;
